(() => {
  const propertyName = '_ZapietId';
  const pending = new WeakSet();
  const replaying = new WeakSet();

  function normalizedProperties(properties) {
    const entries = [];
    for (const key of Object.keys(properties || {}).sort()) {
      if (key === propertyName) continue;
      const value = properties[key];
      if (value === null || value === undefined || value === '') continue;
      if (!['string', 'number', 'boolean'].includes(typeof value)) return null;
      entries.push([key, String(value)]);
    }
    return JSON.stringify(entries);
  }

  function inheritedId(cart, item) {
    const properties = normalizedProperties(item.properties);
    if (properties === null) return null;
    const plan = String(item.selling_plan || '');
    const ids = new Set();
    for (const line of cart.items || []) {
      if (String(line.variant_id) !== String(item.id)) continue;
      if (String(line.selling_plan_allocation?.selling_plan?.id || '') !== plan) continue;
      if (line.parent_relationship) continue;
      if (normalizedProperties(line.properties) !== properties) continue;
      const id = line.properties?.[propertyName];
      if (typeof id === 'string' && id.length > 0) ids.add(id);
    }
    return ids.size === 1 ? [...ids][0] : null;
  }

  async function prepareItem(item) {
    if (!item.id || item.parent_id || item.parent_line_key) return item;
    if (Object.prototype.hasOwnProperty.call(item.properties || {}, propertyName)) return item;
    if (normalizedProperties(item.properties) === null) return item;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 2000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await window.fetch(`${root}cart.js`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return item;
      const id = inheritedId(await response.json(), item);
      if (!id) return item;
      return { ...item, properties: { ...item.properties, [propertyName]: id } };
    } catch {
      return item;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  window.TorontoPetsCart = { prepareItem };

  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
    if (!form.matches('.product-single__form') || typeof form.requestSubmit !== 'function') return;
    const action = new URL(form.action, window.location.href);
    if (action.origin !== window.location.origin || !/\/cart\/add\/?$/.test(action.pathname)) return;
    if (replaying.has(form)) {
      replaying.delete(form);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending.has(form)) return;
    pending.add(form);
    form.querySelector('input[data-toronto-pets-delivery-property]')?.remove();
    const data = new FormData(form);
    const properties = Object.create(null);
    let hasFile = false;
    for (const [name, value] of data.entries()) {
      const property = name.match(/^properties\[(.+)\]$/);
      if (!property) continue;
      if (typeof value !== 'string') {
        hasFile = true;
        continue;
      }
      properties[property[1]] = value;
    }
    const item = {
      id: data.get('id'),
      selling_plan: data.get('selling_plan'),
      properties,
      parent_id: data.get('parent_id'),
      parent_line_key: data.get('parent_line_key')
    };
    const submitter = event.submitter;
    const busy = form.getAttribute('aria-busy');
    form.setAttribute('aria-busy', 'true');
    (hasFile ? Promise.resolve(item) : prepareItem(item)).then(async prepared => {
      // Let the browser finish its original submit event before replaying it.
      await new Promise(resolve => window.setTimeout(resolve, 0));
      const id = prepared.properties?.[propertyName];
      if (!Object.prototype.hasOwnProperty.call(properties, propertyName) && typeof id === 'string') {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${propertyName}]`;
        input.value = id;
        input.dataset.torontoPetsDeliveryProperty = 'true';
        form.appendChild(input);
      }
      replaying.add(form);
      pending.delete(form);
      if (submitter?.form === form) form.requestSubmit(submitter);
      else form.requestSubmit();
    }).finally(() => {
      pending.delete(form);
      replaying.delete(form);
      if (busy === null) form.removeAttribute('aria-busy');
      else form.setAttribute('aria-busy', busy);
    });
  }, true);
})();
